import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowLeft, Search, Filter, ArrowUpDown, Plus, Package, Edit, Trash2, Box, Download, RefreshCw, Store } from "lucide-react";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import {
  CatalogPackageBuilderDialog,
  CatalogPackageSystemsTab,
  createInitialPackageEditorState,
  type CatalogPackageGroup,
} from "@/components/catalog/CatalogPackageSystems";


interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  subcategory: string | null;
  type: string | null;
  manufacturer: string | null;
  material: string | null;
  unit_type: string | null;
  unit_price: number | null;
  material_cost: number | null;
  labor_cost: number | null;
  is_active: boolean;
  is_system_template: boolean;
  tier?: string | null;
  xactimate_code?: string | null;
}

type PackageGroup = CatalogPackageGroup;

interface SupplierProduct {
  id: string;
  product_sku: string;
  product_name: string;
  category: string | null;
  unit_of_measure: string | null;
  unit_price: number | null;
  list_price: number | null;
  quantity_available: number | null;
  supplier_name: string;
}

const Catalog = () => {
  const navigate = useNavigate();
  const { orgId } = useAuth();
  const [activeTab, setActiveTab] = useState<"items" | "packages" | "supplier">("items");
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [packages, setPackages] = useState<PackageGroup[]>([]);
  const [supplierProducts, setSupplierProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [packageEditor, setPackageEditor] = useState(createInitialPackageEditorState());
  const [packageSaving, setPackageSaving] = useState(false);

  // Filter states
  const [manufacturerFilter, setManufacturerFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [unitFilter, setUnitFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    unit_type: "",
    material_cost: "",
    labor_cost: "",
    markup: "20"
  });

  useEffect(() => {
    if (orgId) {
      loadData();
    }
  }, [orgId]);

  const loadData = async () => {
    if (!orgId) return;
    
    try {
      // Load items, packages, and supplier products in parallel
      const [itemsResult, packagesResult, supplierResult] = await Promise.all([
        supabase.from('catalog_items').select('*').or(`org_id.eq.${orgId},is_system_template.eq.true`).order('name'),
        supabase
          .from('package_groups')
          .select('*, package_group_items(*, catalog_item:catalog_items(*))')
          .or(`org_id.eq.${orgId},is_system.eq.true`)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase.from('supplier_products_cache').select('*').eq('org_id', orgId).order('product_name')
      ]);

      setItems(itemsResult.data || []);
      setPackages(((packagesResult.data as any[]) || []).map((pkg) => ({
        ...pkg,
        items: [...(pkg.package_group_items || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
      })));
      setSupplierProducts(supplierResult.data || []);
    } catch (error: any) {
      console.error("Error loading catalog:", error);
      toast.error("Failed to load catalog");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncProducts = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('roofhub-pricing', {
        body: { action: 'sync-products' }
      });
      
      if (error) throw error;
      
      toast.success(data.message || 'Products synced successfully');
      loadData();
    } catch (error: any) {
      console.error("Sync error:", error);
      toast.error("Failed to sync products");
    } finally {
      setSyncing(false);
    }
  };

  const handleImportToInventory = async (product: SupplierProduct) => {
    if (!orgId) return;
    
    try {
      const { error } = await supabase.from('catalog_items').insert({
        org_id: orgId,
        name: product.product_name,
        category: product.category || 'Roofing',
        unit_type: product.unit_of_measure,
        material_cost: product.unit_price || 0,
        labor_cost: 0,
        unit_price: product.unit_price || 0,
        manufacturer: product.product_name.includes('GAF') ? 'GAF' : 
                     product.product_name.includes('Owens Corning') ? 'Owens Corning' : 
                     product.product_name.includes('CertainTeed') ? 'CertainTeed' : 'Generic',
        description: `SKU: ${product.product_sku} | Imported from SRS RoofHub`
      });
      
      if (error) throw error;
      
      toast.success(`${product.product_name} added to your catalog`);
      loadData();
    } catch (error: any) {
      console.error("Import error:", error);
      toast.error("Failed to import product");
    }
  };

  const handleImportAllProducts = async () => {
    if (!orgId || supplierProducts.length === 0) return;
    
    try {
      const catalogItems = supplierProducts.map(product => ({
        org_id: orgId,
        name: product.product_name,
        category: product.category || 'Roofing',
        unit_type: product.unit_of_measure,
        material_cost: product.unit_price || 0,
        labor_cost: 0,
        unit_price: product.unit_price || 0,
        manufacturer: product.product_name.includes('GAF') ? 'GAF' : 
                     product.product_name.includes('Owens Corning') ? 'Owens Corning' : 
                     product.product_name.includes('CertainTeed') ? 'CertainTeed' : 'Generic',
        description: `SKU: ${product.product_sku} | Imported from SRS RoofHub`
      }));
      
      const { error } = await supabase.from('catalog_items').insert(catalogItems);
      
      if (error) throw error;
      
      toast.success(`${supplierProducts.length} products imported to your catalog`);
      loadData();
    } catch (error: any) {
      console.error("Bulk import error:", error);
      toast.error("Failed to import products");
    }
  };

  const handleSaveItem = async () => {
    if (!orgId) {
      toast.error("Organization not found");
      return;
    }

    try {
      const materialCost = parseFloat(formData.material_cost) || 0;
      const laborCost = parseFloat(formData.labor_cost) || 0;
      const markup = parseFloat(formData.markup) || 0;
      const unitPrice = (materialCost + laborCost) * (1 + markup / 100);

      const itemData = {
        org_id: orgId,
        name: formData.name,
        category: formData.category,
        description: formData.description || null,
        unit_type: formData.unit_type,
        material_cost: materialCost,
        labor_cost: laborCost,
        unit_price: unitPrice
      };

      if (editingItem) {
        const { error } = await supabase
          .from('catalog_items')
          .update(itemData)
          .eq('id', editingItem.id);
        if (error) throw error;
        toast.success("Item updated successfully");
      } else {
        const { error } = await supabase
          .from('catalog_items')
          .insert(itemData);
        if (error) throw error;
        toast.success("Item created successfully");
      }

      setItemDialogOpen(false);
      setEditingItem(null);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error("Error saving item:", error);
      toast.error("Failed to save item");
    }
  };

  const handleSavePackage = async ({
    sourceId,
    cloneMode,
    values,
  }: {
    sourceId?: string;
    cloneMode: boolean;
    values: {
      name: string;
      category: string;
      manufacturer: string;
      tier: "good" | "better" | "best" | "";
      description: string;
      lines: Array<{
        id?: string;
        catalog_item_id: string | null;
        section: "material" | "labor";
        default_qty_formula: string;
        sort_order: number;
      }>;
    };
  }) => {
    if (!orgId) {
      toast.error("Organization not found");
      return;
    }

    if (!values.name.trim()) {
      toast.error("Package name is required");
      return;
    }

    try {
      setPackageSaving(true);

      const isEdit = !!(sourceId && !cloneMode);
      let packageId = sourceId;

      if (isEdit) {
        const { error } = await supabase
          .from('package_groups')
          .update({
            name: values.name,
            category: values.category || null,
            manufacturer: values.manufacturer || null,
            tier: values.tier || null,
            description: values.description || null,
          })
          .eq('id', sourceId);

        if (error) throw error;

        const { error: deleteItemsError } = await supabase
          .from('package_group_items')
          .delete()
          .eq('package_group_id', sourceId);

        if (deleteItemsError) throw deleteItemsError;
      } else {
        const { data: packageData, error: packageError } = await supabase
          .from('package_groups')
          .insert({
            org_id: orgId,
            name: values.name,
            category: values.category || null,
            manufacturer: values.manufacturer || null,
            tier: values.tier || null,
            description: values.description || null,
            is_system: false,
            sku: `usr-${Date.now().toString(36)}`,
            cloned_from_id: cloneMode ? sourceId || null : null,
          })
          .select('id')
          .single();

        if (packageError) throw packageError;
        packageId = packageData.id;
      }

      const validLines = values.lines.filter((line) => line.catalog_item_id);
      if (validLines.length > 0 && packageId) {
        const { error: itemsError } = await supabase
          .from('package_group_items')
          .insert(
            validLines.map((line, index) => ({
              package_group_id: packageId,
              catalog_item_id: line.catalog_item_id,
              section: line.section,
              default_qty_formula: line.default_qty_formula || '1',
              sort_order: index,
              quantity: 1,
            }))
          );
        if (itemsError) throw itemsError;
      }

      toast.success(isEdit ? "Package system updated" : cloneMode ? "Package system cloned" : "Package system created");
      setPackageEditor(createInitialPackageEditorState());
      await loadData();
    } catch (error: any) {
      console.error("Error saving package:", error);
      toast.error("Failed to save package system");
    } finally {
      setPackageSaving(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    try {
      const { error } = await supabase
        .from('catalog_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success("Item deleted successfully");
      loadData();
    } catch (error: any) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleDeletePackage = async (pkg: PackageGroup) => {
    if (pkg.is_system) {
      toast.error("Prebuilt package systems can't be deleted. Clone it instead.");
      return;
    }

    if (!confirm(`Are you sure you want to delete \"${pkg.name}\"?`)) return;

    try {
      const { error } = await supabase
        .from('package_groups')
        .delete()
        .eq('id', pkg.id);

      if (error) throw error;
      toast.success("Package system deleted successfully");
      loadData();
    } catch (error: any) {
      console.error("Error deleting package:", error);
      toast.error("Failed to delete package system");
    }
  };

  const openEditDialog = (item: CatalogItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category || "",
      description: item.description || "",
      unit_type: item.unit_type || "",
      material_cost: item.material_cost?.toString() || "",
      labor_cost: item.labor_cost?.toString() || "",
      markup: "20"
    });
    setItemDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category: "",
      description: "",
      unit_type: "",
      material_cost: "",
      labor_cost: "",
      markup: "20"
    });
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesManufacturer = manufacturerFilter === "all" || item.manufacturer === manufacturerFilter;
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesUnit = unitFilter === "all" || item.unit_type === unitFilter;
    
    let matchesPrice = true;
    if (priceFilter !== "all" && item.unit_price) {
      const price = item.unit_price;
      if (priceFilter === "under10") matchesPrice = price < 10;
      else if (priceFilter === "10-50") matchesPrice = price >= 10 && price <= 50;
      else if (priceFilter === "50-100") matchesPrice = price >= 50 && price <= 100;
      else if (priceFilter === "100-500") matchesPrice = price >= 100 && price <= 500;
      else if (priceFilter === "over500") matchesPrice = price > 500;
    }

    return matchesSearch && matchesManufacturer && matchesCategory && matchesUnit && matchesPrice;
  });

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.tier?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || pkg.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const sellingPrice = () => {
    const material = parseFloat(formData.material_cost) || 0;
    const labor = parseFloat(formData.labor_cost) || 0;
    const markup = parseFloat(formData.markup) || 0;
    return ((material + labor) * (1 + markup / 100)).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">Loading catalog...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-foreground mb-2">Catalog Manager</h1>
          <p className="text-muted-foreground">
            Create and manage line items and commonly used package groups for your proposals
          </p>
        </div>

        {/* Search and Actions */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={activeTab === "packages"
                ? "Search package systems by manufacturer, tier, or name..."
                : "Search by name, description, or category..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {activeTab !== "packages" && (
            <>
              <Button
                variant="outline"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button
                variant="outline"
                onClick={() => setSortOpen(!sortOpen)}
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Sort
              </Button>
            </>
          )}
        </div>

        {/* Item Count */}
        <div className="text-sm text-muted-foreground mb-4">
          Showing {activeTab === "items" ? filteredItems.length : activeTab === "packages" ? filteredPackages.length : supplierProducts.length} of {activeTab === "items" ? items.length : activeTab === "packages" ? packages.length : supplierProducts.length} items
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant={activeTab === "items" ? "default" : "outline"}
            onClick={() => setActiveTab("items")}
            className={activeTab === "items" ? "bg-primary text-primary-foreground" : ""}
          >
            Line Items
          </Button>
          <Button
            variant={activeTab === "packages" ? "default" : "outline"}
            onClick={() => setActiveTab("packages")}
            className={activeTab === "packages" ? "bg-primary text-primary-foreground" : ""}
          >
            Package Systems
          </Button>
          <Button
            variant={activeTab === "supplier" ? "default" : "outline"}
            onClick={() => setActiveTab("supplier")}
            className={activeTab === "supplier" ? "bg-primary text-primary-foreground" : ""}
          >
            <Store className="h-4 w-4 mr-2" />
            SRS RoofHub ({supplierProducts.length})
          </Button>
        </div>

        {/* Category Tabs - Only show when on items tab */}
        {activeTab === "items" && (
          <div className="flex items-center gap-2 mb-4 border-b overflow-x-auto pb-2">
            <Button
              variant={categoryFilter === "all" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("all")}
              size="sm"
            >
              All Categories
            </Button>
            <Button
              variant={categoryFilter === "Roofing" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Roofing")}
              size="sm"
            >
              Roofing
            </Button>
            <Button
              variant={categoryFilter === "Siding" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Siding")}
              size="sm"
            >
              Siding
            </Button>
            <Button
              variant={categoryFilter === "Trim" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Trim")}
              size="sm"
            >
              Trim
            </Button>
            <Button
              variant={categoryFilter === "Gutters" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Gutters")}
              size="sm"
            >
              Gutters
            </Button>
            <Button
              variant={categoryFilter === "Windows & Doors" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Windows & Doors")}
              size="sm"
            >
              Windows & Doors
            </Button>
            <Button
              variant={categoryFilter === "Insulation" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Insulation")}
              size="sm"
            >
              Insulation
            </Button>
            <Button
              variant={categoryFilter === "Tools & Safety" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Tools & Safety")}
              size="sm"
            >
              Tools & Safety
            </Button>
            <Button
              variant={categoryFilter === "Warranty" ? "default" : "ghost"}
              onClick={() => setCategoryFilter("Warranty")}
              size="sm"
            >
              Warranty
            </Button>
          </div>
        )}

        {/* Manufacturer Filter - Only show when on Roofing category */}
        {activeTab === "items" && (categoryFilter === "all" || categoryFilter === "Roofing") && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-muted-foreground">Filter by:</span>
            <Button
              variant={manufacturerFilter === "all" ? "secondary" : "outline"}
              onClick={() => setManufacturerFilter("all")}
              size="sm"
            >
              All
            </Button>
            <Button
              variant={manufacturerFilter === "GAF" ? "secondary" : "outline"}
              onClick={() => setManufacturerFilter("GAF")}
              size="sm"
            >
              GAF
            </Button>
            <Button
              variant={manufacturerFilter === "Owens Corning" ? "secondary" : "outline"}
              onClick={() => setManufacturerFilter("Owens Corning")}
              size="sm"
            >
              Owens Corning
            </Button>
            <Button
              variant={manufacturerFilter === "CertainTeed" ? "secondary" : "outline"}
              onClick={() => setManufacturerFilter("CertainTeed")}
              size="sm"
            >
              CertainTeed
            </Button>
            <Button
              variant={manufacturerFilter === "Generic" ? "secondary" : "outline"}
              onClick={() => setManufacturerFilter("Generic")}
              size="sm"
            >
              Generic
            </Button>
          </div>
        )}

        {/* Description and Action Button */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {activeTab === "items"
              ? "Individual items that can be added to proposals or grouped into package groups"
              : activeTab === "packages"
              ? "Prebuilt and custom Good / Better / Best systems grouped by manufacturer"
              : "Products synced from your SRS RoofHub supplier account with your pricing"}
          </p>
          {activeTab === "packages" ? null : activeTab === "supplier" ? (
            <div className="flex gap-2">
              <Button
                onClick={handleSyncProducts}
                disabled={syncing}
                variant="outline"
                className="border-primary/50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Products'}
              </Button>
              {supplierProducts.length > 0 && (
                <Button
                  onClick={handleImportAllProducts}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Import All to Catalog
                </Button>
              )}
            </div>
          ) : (
            <Button
              onClick={() => activeTab === "items"
                ? setItemDialogOpen(true)
                : setPackageEditor({ open: true, source: null, cloneMode: false })}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              {activeTab === "items" ? "New Item" : "New Package System"}
            </Button>
          )}
        </div>

        {/* Content */}
        {activeTab === "items" ? (
          filteredItems.length === 0 ? (
            <Card className="bg-card">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Box className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery || categoryFilter !== "all" || priceFilter !== "all"
                    ? "No items found matching your filters"
                    : "No items yet. Create your first item!"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <Card key={item.id} className="bg-card hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{item.name}</h3>
                        {item.category && (
                          <p className="text-xs text-muted-foreground mt-1">{item.category}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditDialog(item)}
                          className="h-8 w-8"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteItem(item.id)}
                          className="h-8 w-8 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                    )}
                     <div className="space-y-1 text-sm">
                       {(item.manufacturer || item.tier || item.xactimate_code) && (
                         <div className="flex flex-wrap gap-2 pb-1">
                           {item.manufacturer && <Badge variant="secondary">{item.manufacturer}</Badge>}
                           {item.tier && <Badge variant="outline">{item.tier}</Badge>}
                           {item.xactimate_code && <Badge variant="outline">{item.xactimate_code}</Badge>}
                         </div>
                       )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Unit:</span>
                        <span className="text-foreground">{item.unit_type || "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price:</span>
                        <span className="text-foreground font-semibold">
                          ${item.unit_price?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : activeTab === "packages" ? (
          <CatalogPackageSystemsTab
            packages={packages}
            catalogItems={items}
            searchQuery={searchQuery}
            categoryFilter={categoryFilter}
            loading={loading}
            saving={packageSaving}
            onCreate={() => setPackageEditor({ open: true, source: null, cloneMode: false })}
            onEdit={(pkg) => {
              if (pkg.is_system) {
                // Prebuilt = clone-on-edit. Reuse existing org clone if present.
                const existingClone = packages.find(p => !p.is_system && p.cloned_from_id === pkg.id);
                if (existingClone) {
                  setPackageEditor({ open: true, source: existingClone, cloneMode: false });
                  toast.info("Editing your saved copy of this prebuilt system");
                } else {
                  setPackageEditor({ open: true, source: pkg, cloneMode: true });
                  toast.info("Saving will create your own editable copy of this prebuilt system");
                }
                return;
              }
              setPackageEditor({ open: true, source: pkg, cloneMode: false });
            }}
            onClone={(pkg) => setPackageEditor({ open: true, source: pkg, cloneMode: true })}
            onDelete={handleDeletePackage}
          />
        ) : (
          // SRS RoofHub Supplier Products Tab
          supplierProducts.length === 0 ? (
            <Card className="bg-card">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Store className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  No products synced yet. Connect and sync from SRS RoofHub.
                </p>
                <Button onClick={handleSyncProducts} disabled={syncing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync Products Now'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {supplierProducts.map((product) => (
                <Card key={product.id} className="bg-card hover:border-primary/50 transition-colors border-l-4 border-l-primary">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{product.product_name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {product.category} • SKU: {product.product_sku}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImportToInventory(product)}
                        className="h-8"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Import
                      </Button>
                    </div>
                    <div className="space-y-1 text-sm mt-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Unit:</span>
                        <span className="text-foreground">{product.unit_of_measure || "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Your Price:</span>
                        <span className="font-semibold text-primary">
                          ${product.unit_price?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Available:</span>
                        <span className="text-foreground">{product.quantity_available || "N/A"}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}

        {/* Filters Sheet */}
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="py-6 space-y-6">
              <div>
                <Label className="text-base font-semibold mb-3 block">Manufacturer</Label>
                <RadioGroup value={manufacturerFilter} onValueChange={setManufacturerFilter}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="mfr-all" />
                    <Label htmlFor="mfr-all" className="font-normal">All Manufacturers</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="GAF" id="mfr-gaf" />
                    <Label htmlFor="mfr-gaf" className="font-normal">GAF</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Owens Corning" id="mfr-oc" />
                    <Label htmlFor="mfr-oc" className="font-normal">Owens Corning</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="CertainTeed" id="mfr-ct" />
                    <Label htmlFor="mfr-ct" className="font-normal">CertainTeed</Label>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              <div>
                <Label className="text-base font-semibold mb-3 block">Category</Label>
                <RadioGroup value={categoryFilter} onValueChange={setCategoryFilter}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="cat-all" />
                    <Label htmlFor="cat-all" className="font-normal">All Categories</Label>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              <div>
                <Label className="text-base font-semibold mb-3 block">Type</Label>
                <RadioGroup value={typeFilter} onValueChange={setTypeFilter}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="type-all" />
                    <Label htmlFor="type-all" className="font-normal">All Types</Label>
                  </div>
                  {activeTab === "items" && (
                    <>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="items" id="type-items" />
                        <Label htmlFor="type-items" className="font-normal">Items Only</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="groups" id="type-groups" />
                        <Label htmlFor="type-groups" className="font-normal">Material Groups Only</Label>
                      </div>
                    </>
                  )}
                </RadioGroup>
              </div>

              <Separator />

              <div>
                <Label className="text-base font-semibold mb-3 block">Unit Type</Label>
                <RadioGroup value={unitFilter} onValueChange={setUnitFilter}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="unit-all" />
                    <Label htmlFor="unit-all" className="font-normal">All Units</Label>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              <div>
                <Label className="text-base font-semibold mb-3 block">Price Range</Label>
                <RadioGroup value={priceFilter} onValueChange={setPriceFilter}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="price-all" />
                    <Label htmlFor="price-all" className="font-normal">All Prices</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="under10" id="price-under10" />
                    <Label htmlFor="price-under10" className="font-normal">Under $10</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="10-50" id="price-10-50" />
                    <Label htmlFor="price-10-50" className="font-normal">$10 - $50</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="50-100" id="price-50-100" />
                    <Label htmlFor="price-50-100" className="font-normal">$50 - $100</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="100-500" id="price-100-500" />
                    <Label htmlFor="price-100-500" className="font-normal">$100 - $500</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="over500" id="price-over500" />
                    <Label htmlFor="price-over500" className="font-normal">Over $500</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* New Item Dialog */}
        <Dialog open={itemDialogOpen} onOpenChange={(open) => {
          setItemDialogOpen(open);
          if (!open) {
            setEditingItem(null);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit Item" : "New Item"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  placeholder="e.g., Asphalt Shingles"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Trade *</Label>
                <Input
                  placeholder="e.g., Roofing, Plumbing, Electrical"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of the item"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Input
                  placeholder="sq ft"
                  value={formData.unit_type}
                  onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter any unit type (sq ft, linear ft, each, etc.)
                </p>
              </div>
              <div>
                <Label>Unit Cost ($/unit)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.material_cost}
                  onChange={(e) => setFormData({ ...formData, material_cost: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Your cost per unit</p>
              </div>
              <div>
                <Label>Markup %</Label>
                <Input
                  type="number"
                  step="1"
                  placeholder="0.00"
                  value={formData.markup}
                  onChange={(e) => setFormData({ ...formData, markup: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Profit markup %</p>
              </div>
              <div>
                <Label>Selling Price</Label>
                <Input
                  value={sellingPrice()}
                  disabled
                  className="bg-muted font-semibold"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-calculated</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setItemDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveItem} className="bg-primary hover:bg-primary/90">
                <Package className="h-4 w-4 mr-2" />
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <CatalogPackageBuilderDialog
          open={packageEditor.open}
          source={packageEditor.source}
          cloneMode={packageEditor.cloneMode}
          catalogItems={items}
          saving={packageSaving}
          onClose={() => setPackageEditor(createInitialPackageEditorState())}
          onSave={handleSavePackage}
        />
      </div>
    </div>
  );
};

export default Catalog;